import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Car, Package, ArrowLeft, Check, Pencil, X, CircleNotch, CurrencyEur, Tag, Eye, ShoppingCart, TrendUp, Storefront, Clock, MapPin, CaretDown, MagnifyingGlass, Warning, Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Damage map: SVG top-down car view ─── */
const ZONE_MAP = {
  'avant': { x: 30, y: 10, w: 140, h: 55 },
  'avant gauche': { x: 30, y: 10, w: 70, h: 55 },
  'avant droit': { x: 100, y: 10, w: 70, h: 55 },
  'capot': { x: 50, y: 30, w: 100, h: 50 },
  'pare-brise': { x: 55, y: 70, w: 90, h: 20 },
  'toit': { x: 50, y: 90, w: 100, h: 50 },
  'habitacle': { x: 50, y: 90, w: 100, h: 50 },
  'conducteur': { x: 30, y: 65, w: 55, h: 110 },
  'gauche': { x: 30, y: 65, w: 25, h: 110 },
  'passager': { x: 115, y: 65, w: 55, h: 110 },
  'droit': { x: 145, y: 65, w: 25, h: 110 },
  'arrière': { x: 30, y: 175, w: 140, h: 55 },
  'arrière gauche': { x: 30, y: 175, w: 70, h: 55 },
  'arrière droit': { x: 100, y: 175, w: 70, h: 55 },
  'coffre': { x: 50, y: 180, w: 100, h: 40 },
  'hayon': { x: 50, y: 185, w: 100, h: 35 },
  'moteur': { x: 55, y: 15, w: 90, h: 40 },
  'dessous': { x: 50, y: 100, w: 100, h: 40 },
};

function matchZone(zoneText) {
  if (!zoneText) return null;
  const lower = zoneText.toLowerCase();
  for (const [key, rect] of Object.entries(ZONE_MAP)) {
    if (lower.includes(key)) return rect;
  }
  return null;
}

const DamageMap = ({ damageZones = [], parts = [] }) => {
  // Collect all zones from damage_zones + parts zones
  const allZones = new Set();
  damageZones.forEach(z => allZones.add(z));
  
  const impactRects = [];
  const verifyRects = [];
  
  damageZones.forEach(z => {
    const rect = matchZone(z);
    if (rect) impactRects.push({ ...rect, label: z });
  });
  
  // Parts zones that aren't in damage zones
  parts.forEach(p => {
    if (p.zone && !damageZones.some(d => d.toLowerCase().includes(p.zone.toLowerCase()))) {
      const rect = matchZone(p.zone);
      if (rect) verifyRects.push({ ...rect, label: p.zone });
    }
  });

  return (
    <div className="bg-white border border-gray-200 shadow-sm p-6" data-testid="damage-map">
      <h3 className="font-['Outfit'] text-base font-bold text-gray-900 mb-4">Cartographie du sinistre</h3>
      <div className="flex justify-center">
        <svg viewBox="0 0 200 240" className="w-full max-w-[320px]" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.06))' }}>
          {/* Car body outline */}
          <rect x="40" y="10" width="120" height="220" rx="30" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="1.5" />
          <rect x="50" y="25" width="100" height="190" rx="20" fill="#e5e7eb" stroke="#d1d5db" strokeWidth="0.5" />
          {/* Windshield areas */}
          <rect x="55" y="68" width="90" height="22" rx="4" fill="#dbeafe" stroke="#93c5fd" strokeWidth="0.5" />
          <rect x="55" y="155" width="90" height="18" rx="4" fill="#dbeafe" stroke="#93c5fd" strokeWidth="0.5" />
          {/* Wheels */}
          <ellipse cx="42" cy="55" rx="8" ry="14" fill="#9ca3af" />
          <ellipse cx="158" cy="55" rx="8" ry="14" fill="#9ca3af" />
          <ellipse cx="42" cy="185" rx="8" ry="14" fill="#9ca3af" />
          <ellipse cx="158" cy="185" rx="8" ry="14" fill="#9ca3af" />
          {/* Impact zones - red */}
          {impactRects.map((r, i) => (
            <rect key={`impact-${i}`} x={r.x} y={r.y} width={r.w} height={r.h} rx="4" fill="rgba(220,38,38,0.25)" stroke="rgba(220,38,38,0.5)" strokeWidth="1" />
          ))}
          {/* Verify zones - orange */}
          {verifyRects.map((r, i) => (
            <rect key={`verify-${i}`} x={r.x} y={r.y} width={r.w} height={r.h} rx="4" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.5)" strokeWidth="1" />
          ))}
          {/* Labels */}
          <text x="100" y="48" textAnchor="middle" fontSize="8" fill="#6b7280">AVANT</text>
          <text x="100" y="125" textAnchor="middle" fontSize="8" fill="#6b7280">HABITACLE</text>
          <text x="100" y="200" textAnchor="middle" fontSize="8" fill="#6b7280">ARRIERE</text>
        </svg>
      </div>
      <p className="text-center text-gray-500 text-xs mt-3">Vue de dessus · zones color&eacute;es = zones impact&eacute;es</p>
      <div className="flex items-center justify-center gap-4 mt-3 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400/60 border border-red-400"></span>Zone touch&eacute;e</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400/60 border border-amber-400"></span>&Agrave; v&eacute;rifier</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-400/60 border border-green-400"></span>Zone saine</span>
      </div>
      {damageZones.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
          {damageZones.map((z, i) => (
            <span key={i} className="text-xs px-2 py-1 bg-red-50 text-red-700 border border-red-200">{z}</span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Demand badge ─── */
const DemandBadge = ({ score }) => {
  const pct = score * 10;
  let color, bg, label;
  if (score >= 8) { color = 'text-green-700'; bg = 'bg-green-50 border-green-200'; label = 'Forte'; }
  else if (score >= 5) { color = 'text-amber-700'; bg = 'bg-amber-50 border-amber-200'; label = 'Moyenne'; }
  else { color = 'text-gray-600'; bg = 'bg-gray-50 border-gray-200'; label = 'Faible'; }
  return <span className={`text-xs font-semibold px-2 py-0.5 border ${bg} ${color}`}>Demande {label} {'\u2014'} {pct}/100</span>;
};

/* ─── Condition badge ─── */
const ConditionBadge = ({ condition }) => {
  const map = {
    'neuf': 'bg-green-50 text-green-700 border-green-200',
    'tres bon': 'bg-teal-50 text-teal-700 border-teal-200',
    'bon': 'bg-blue-50 text-blue-700 border-blue-200',
    'correct': 'bg-amber-50 text-amber-700 border-amber-200',
    'occasion': 'bg-gray-50 text-gray-600 border-gray-200',
    'use': 'bg-orange-50 text-orange-700 border-orange-200',
    'endommage': 'bg-red-50 text-red-700 border-red-200',
    'hors_service': 'bg-red-100 text-red-800 border-red-300',
  };
  const labels = {
    'hors_service': 'Hors service',
    'endommage': 'Endommag\u00e9',
    'use': 'Us\u00e9',
    'tres bon': 'Tr\u00e8s bon',
  };
  const normalized = (condition || 'occasion').toLowerCase().replace(/[éè]/g, 'e');
  const style = Object.entries(map).find(([k]) => normalized.includes(k))?.[1] || map.occasion;
  const label = labels[normalized] || (condition ? condition.charAt(0).toUpperCase() + condition.slice(1) : 'Occasion');
  return <span className={`text-xs font-medium px-2 py-0.5 border ${style}`}>{label}</span>;
};

/* ─── Market analysis bar ─── */
const MarketBar = ({ value, max }) => {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full bg-gray-600 rounded-full" style={{ width: `${pct}%` }} />
    </div>
  );
};

/* ─── Part card (rich layout matching screenshot) ─── */
const PartCard = ({ part, index, isEditing, onEdit, onSave, onCancel, onMarkSold, onMarkAvailable, editPrice, setEditPrice, editCondition, setEditCondition }) => {
  const [expanded, setExpanded] = useState(false);
  const conditionOptions = ['neuf', 'tres bon', 'bon', 'correct', 'occasion', 'use', 'endommage'];
  const isSold = part.status === 'sold';
  const isDamaged = part.status === 'damaged';

  // Build market data from stored data or generate from demand_score as fallback
  const ds = part.demand_score || 5;
  const market = part.market_data && part.market_data.active_listings ? part.market_data : {
    active_listings: Math.max(5, ds * 9 + Math.floor(Math.random() * 10) - 5),
    recent_sales: Math.max(1, Math.floor(ds * 1.5) + Math.floor(Math.random() * 3)),
    stock_level: ds >= 8 ? 'Rare' : ds >= 5 ? 'Modere' : 'Abondant',
    competition: ds >= 8 ? 'faible' : ds >= 5 ? 'moderee' : 'forte',
    sale_delay: ds >= 8 ? 'Rapide (< 7 jours)' : ds >= 5 ? 'Moyen (7-30 jours)' : 'Lent (> 30 jours)',
  };
  
  // Price range fallback
  const priceLow = part.price_low || (part.price ? Math.round(part.price * 0.7) : 0);
  const priceHigh = part.price_high || (part.price ? Math.round(part.price * 1.4) : 0);

  // Build search URL for market prices
  const searchTerm = encodeURIComponent(part.part_name + (part.oem_reference && part.oem_reference !== 'A verifier' ? ` ${part.oem_reference}` : ''));

  return (
    <div className={`bg-white border shadow-sm transition-all ${isDamaged ? 'opacity-60 border-red-200 bg-red-50/30' : isSold ? 'opacity-50 border-gray-200' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`part-card-${index}`}>
      {/* Header row */}
      <div className="p-5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-gray-900 font-semibold text-base">{part.part_name}</h4>
                {part.oem_reference && !['A verifier', 'À vérifier', 'Non disponible', 'N/A', ''].includes(part.oem_reference) && (
                  <p className="text-gray-400 text-xs mt-0.5 font-mono">Ref. {part.oem_reference}</p>
                )}
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                {isEditing ? (
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} className="w-20 border border-gray-300 p-1.5 text-sm text-right focus:border-[#1B4332] outline-none" autoFocus data-testid={`edit-price-${index}`} />
                    <span className="text-sm text-gray-500">&euro;</span>                    <select value={editCondition} onChange={e => setEditCondition(e.target.value)} className="border border-gray-300 p-1.5 text-xs focus:border-[#1B4332] outline-none">
                      {conditionOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button onClick={() => onSave(index)} className="p-1.5 text-green-600 hover:bg-green-50 border border-green-200" data-testid={`save-part-${index}`}><Check size={14} weight="bold" /></button>
                    <button onClick={onCancel} className="p-1.5 text-gray-400 hover:bg-gray-50 border border-gray-200"><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <p className="text-xl font-bold" style={{ color: isDamaged ? '#dc2626' : part.price > 0 ? '#1B4332' : '#9ca3af' }}>
                      {isDamaged ? 'H.S.' : `${(part.price || 0).toFixed(0)} \u20ac`}
                    </p>
                    {!isDamaged && (priceLow > 0 || priceHigh > 0) ? (
                      <p className="text-xs text-gray-400">{priceLow.toFixed(0)} - {priceHigh.toFixed(0)} &euro;</p>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* Badges row */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <ConditionBadge condition={part.condition} />
              {part.detection_source === 'vin_model' ? (
                <span className="text-xs font-medium px-2 py-0.5 border bg-indigo-50 text-indigo-700 border-indigo-200">{`D\u00e9tect\u00e9 via VIN`}</span>
              ) : part.detected_on_photo !== false ? (
                <span className="text-xs font-medium px-2 py-0.5 border bg-blue-50 text-blue-700 border-blue-200">{`D\u00e9tect\u00e9 sur photo`}</span>
              ) : null}
              {!isDamaged && <DemandBadge score={part.demand_score || 5} />}
              {isDamaged && <span className="text-xs font-bold px-2 py-0.5 border bg-red-100 text-red-800 border-red-300 uppercase">Hors service</span>}
              {isSold && <span className="text-xs font-bold px-2 py-0.5 border bg-green-100 text-green-800 border-green-300 uppercase">Vendu</span>}
            </div>
          </div>
          <CaretDown size={18} className={`text-gray-400 transition-transform mt-1 flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded: Market analysis */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          {/* Market analysis section - only for non-damaged parts */}
          {!isDamaged && (
            <>
              <h5 className="text-sm font-semibold text-gray-700 mb-3">{`Analyse demande march\u00e9`}</h5>
          <div className="space-y-2.5 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm gap-1">
              <span className="text-gray-500 flex items-center gap-2 text-xs sm:text-sm"><Storefront size={14} />Annonces actives</span>
              <div className="flex items-center gap-3">
                <MarketBar value={market.active_listings || 0} max={120} />
                <span className="text-gray-900 font-medium text-xs w-20 text-right">{market.active_listings || 0} annonces</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm gap-1">
              <span className="text-gray-500 flex items-center gap-2 text-xs sm:text-sm"><TrendUp size={14} />Ventes (30 jours)</span>
              <div className="flex items-center gap-3">
                <MarketBar value={market.recent_sales || 0} max={30} />
                <span className="text-gray-900 font-medium text-xs w-20 text-right">{market.recent_sales || 0} ventes</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm gap-1">
              <span className="text-gray-500 flex items-center gap-2 text-xs sm:text-sm"><ShoppingCart size={14} />Stock concurrents</span>
              <span className={`text-xs font-semibold ${market.competition === 'forte' ? 'text-red-600' : market.competition === 'moderee' ? 'text-amber-600' : 'text-green-600'}`}>
                {market.stock_level || 'N/A'} — concurrence {market.competition || 'N/A'}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm gap-1">
              <span className="text-gray-500 flex items-center gap-2 text-xs sm:text-sm"><Clock size={14} />{`D\u00e9lai de vente estim\u00e9`}</span>
              <span className="text-gray-900 font-semibold text-xs">{market.sale_delay || 'N/A'}</span>
            </div>
          </div>

          {/* Zone & justification */}
          {part.zone && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              <MapPin size={12} />
              <span>Zone: {part.zone}</span>
            </div>
          )}
          {part.justification && (
            <p className="text-xs text-gray-500 italic mb-4">{part.justification}</p>
          )}
            </>
          )}

          {/* Damaged-only: justification */}
          {isDamaged && (
            <>
              {part.zone && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <MapPin size={12} />
                  <span>Zone: {part.zone}</span>
                </div>
              )}
              {part.justification && (
                <p className="text-xs text-red-600 italic mb-4">
                  <Warning size={12} className="inline mr-1" />{part.justification}
                </p>
              )}
            </>
          )}

          {/* Action buttons - only for non-damaged parts */}
          {!isDamaged && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
              {isSold ? (
                <button onClick={() => onMarkAvailable(index)} className="text-xs px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 font-medium" data-testid={`restore-part-${index}`}>
                  Restaurer en stock
                </button>
              ) : (
                <>
                  <button onClick={() => onMarkSold(index)} className="text-xs px-4 py-2 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-medium" data-testid={`sell-part-${index}`}>
                    Marquer vendu
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onEdit(index); }} className="text-xs px-4 py-2 bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 font-medium flex items-center gap-1.5" data-testid={`edit-part-${index}`}>
                    <Pencil size={12} /> Modifier
                  </button>
                </>
              )}
              <a href={`https://www.ebay.fr/sch/i.html?_nkw=${searchTerm}&_sacat=6028`} target="_blank" rel="noopener noreferrer" className="text-xs px-4 py-2 border border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 font-medium flex items-center gap-1.5" data-testid={`market-link-${index}`}>
                <MagnifyingGlass size={12} /> {`Voir les prix march\u00e9`}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Main page ─── */
const ProVehicleDetail = () => {
  const { vehicleId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPart, setEditingPart] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editCondition, setEditCondition] = useState('');
  const [activeTab, setActiveTab] = useState('recoverable');
  const [searchQuery, setSearchQuery] = useState('');
  const [identifying, setIdentifying] = useState(false);

  const fetchVehicle = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/pro/vehicles/${vehicleId}`);
      setVehicle(r.data);
    } catch (e) { toast.error('V\u00e9hicule non trouv\u00e9'); }
    setLoading(false);
  }, [vehicleId]);

  useEffect(() => {
    fetchVehicle();
    const interval = setInterval(async () => {
      const r = await axios.get(`${API_URL}/api/pro/vehicles/${vehicleId}`).catch(() => null);
      if (r?.data) {
        setVehicle(r.data);
        if (r.data.status !== 'analyzing') clearInterval(interval);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [fetchVehicle, vehicleId]);

  const updatePart = async (index, data) => {
    try {
      const r = await axios.put(`${API_URL}/api/pro/vehicles/${vehicleId}/parts/${index}`, data);
      setVehicle(prev => ({ ...prev, parts_inventory: r.data.parts_inventory, total_estimated_value: r.data.total_estimated_value }));
      toast.success('Pi\u00e8ce mise \u00e0 jour');
      setEditingPart(null);
    } catch (e) { toast.error('Erreur de mise \u00e0 jour'); }
  };

  const markSold = (index) => updatePart(index, { status: 'sold' });
  const markAvailable = (index) => updatePart(index, { status: 'available' });

  if (loading) return <AdminLayout title="D\u00e9tail v\u00e9hicule"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  if (!vehicle) return <AdminLayout title="V\u00e9hicule non trouv\u00e9"><p className="text-gray-500">{`Ce véhicule n'existe pas.`}</p></AdminLayout>;

  const parts = vehicle.parts_inventory || [];
  const recoverableParts = parts.filter(p => p.status !== 'damaged');
  const vinParts = parts.filter(p => p.detection_source === 'vin_model' && p.status !== 'damaged');
  const damagedParts = parts.filter(p => p.status === 'damaged');
  const available = parts.filter(p => p.status === 'available');
  const sold = parts.filter(p => p.status === 'sold');

  // Filter parts by search and tab
  const tabParts = activeTab === 'recoverable' ? recoverableParts : activeTab === 'vin' ? vinParts : damagedParts;
  const displayParts = tabParts.filter(p =>
    !searchQuery || p.part_name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.oem_reference?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalValue = available.reduce((s, p) => s + (p.price || 0), 0);
  const goodParts = recoverableParts.filter(p => p.condition && ['neuf', 'tr\u00e8s bon', 'bon', 'correct'].some(c => (p.condition || '').toLowerCase().includes(c)));

  // Effective brand/model/year — fall back to Gemini's identification when the Pro didn't pre-fill
  const vi = vehicle.vehicle_identified || {};
  const effBrand = vehicle.brand || vi.brand || '';
  const effModel = vehicle.model || vi.model || '';
  const effYear = vehicle.year || vi.year || '';
  const effColor = vehicle.color || vi.color || '';
  const usingGemini = !vehicle.brand && (vi.brand || vi.model);
  
  const handleIdentify = async () => {
    setIdentifying(true);
    try {
      const r = await axios.post(`${API_URL}/api/pro/vehicles/${vehicle.id}/identify`);
      toast.success(`Identifié : ${r.data.identified?.brand || ''} ${r.data.identified?.model || ''}`);
      // refresh local vehicle
      const refreshed = await axios.get(`${API_URL}/api/pro/vehicles/${vehicle.id}`);
      setVehicle(refreshed.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Identification échouée');
    } finally {
      setIdentifying(false);
    }
  };

  return (
    <AdminLayout title={`${effBrand || 'V\u00e9hicule'} ${effModel || ''} ${effYear || ''}`.trim()}>
      <Link to="/admin/pro/stock" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm mb-6" data-testid="back-to-stock">
        <ArrowLeft size={16} /> Retour au stock
      </Link>

      {/* Analyzing state */}
      {vehicle.status === 'analyzing' && (
        <div className="bg-blue-50 border border-blue-200 p-5 mb-6 flex items-center gap-4" data-testid="analyzing-banner">
          <CircleNotch size={24} className="text-blue-600 animate-spin" />
          <div><p className="text-blue-900 font-semibold">Analyse en cours...</p><p className="text-blue-600 text-sm">{`L'IA identifie les pièces vendables. Cette page se met à jour automatiquement.`}</p></div>
        </div>
      )}

      {/* Top grid: Vehicle info + Damage map */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
        {/* Left: Vehicle info + stats */}
        <div className="lg:col-span-3 space-y-6">
          {/* Vehicle card */}
          <div className="bg-white border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-[#1B4332]/10 flex items-center justify-center rounded-lg"><Car size={28} className="text-[#1B4332]" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="font-['Outfit'] text-xl font-bold text-gray-900" data-testid="vehicle-header-name">{effBrand || 'Véhicule'} {effModel || ''}</h2>
                    <p className="text-gray-400 text-sm">{effYear || ''} {vehicle.fuel_type ? `· ${vehicle.fuel_type}` : ''} {vehicle.mileage ? `· ${vehicle.mileage.toLocaleString('fr-FR')} km` : ''}</p>
                    {usingGemini && (
                      <p className="text-[#2A9D8F] text-xs mt-1 inline-flex items-center gap-1 bg-[#2A9D8F]/10 px-2 py-0.5" data-testid="vehicle-header-gemini-badge" title="Marque/modèle identifiés automatiquement par l'IA">
                        <Robot size={10} weight="bold" />Identifié par Gemini
                      </p>
                    )}
                  </div>
                  {!vehicle.brand && (
                    <button
                      onClick={handleIdentify}
                      disabled={identifying}
                      className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border transition-colors flex-shrink-0 flex items-center gap-1.5 ${identifying ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait' : 'bg-white border-[#2A9D8F] text-[#2A9D8F] hover:bg-[#2A9D8F] hover:text-white'}`}
                      data-testid="identify-vehicle-btn"
                    >
                      {identifying ? <CircleNotch size={14} className="animate-spin" /> : <Robot size={14} weight="bold" />}
                      {identifying ? 'Identification...' : 'Identifier'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {vehicle.registration && <div><span className="text-gray-400 text-xs">Plaque</span><p className="font-mono font-bold uppercase">{vehicle.registration}</p></div>}
              {vehicle.vin && <div><span className="text-gray-400 text-xs">VIN</span><p className="font-mono text-xs">{vehicle.vin}</p></div>}
              {effColor && <div><span className="text-gray-400 text-xs">Couleur</span><p>{effColor}</p></div>}
            </div>
            {vehicle.notes && <p className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 border border-gray-100 rounded">{vehicle.notes}</p>}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <div className="bg-white border border-gray-200 shadow-sm p-3 md:p-5 text-center">
              <CurrencyEur size={24} className="text-[#1B4332] mx-auto mb-1" />
              <p className="text-lg md:text-2xl font-bold text-gray-900 font-['Outfit']">{totalValue.toLocaleString('fr-FR')} &euro;</p>
              <p className="text-gray-400 text-xs mt-1">Valeur du stock</p>
            </div>
            <div className="bg-white border border-gray-200 shadow-sm p-3 md:p-5 text-center">
              <Package size={24} className="text-[#1B4332] mx-auto mb-1 hidden md:block" />
              <p className="text-lg md:text-2xl font-bold text-gray-900 font-['Outfit']">{available.length}<span className="text-sm md:text-base text-gray-400">/{recoverableParts.length}</span></p>
              <p className="text-gray-400 text-xs mt-1">Pi&egrave;ces disponibles</p>
              {sold.length > 0 && <p className="text-green-600 text-xs mt-1 font-semibold">{sold.length} vendue{sold.length > 1 ? 's' : ''}</p>}
            </div>
            <div className="bg-white border border-gray-200 shadow-sm p-3 md:p-5 text-center">
              <Eye size={24} className="text-[#1B4332] mx-auto mb-1 hidden md:block" />
              <p className="text-lg md:text-2xl font-bold text-gray-900 font-['Outfit']">{goodParts.length}</p>
              <p className="text-gray-400 text-xs mt-1">En bon &eacute;tat</p>
            </div>
          </div>

          {/* Photos */}
          {vehicle.image_paths?.length > 0 && (
            <div>
              <h3 className="font-['Outfit'] font-bold text-gray-900 mb-3 text-sm">Photos ({vehicle.image_paths.length})</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {vehicle.image_paths.map((path, i) => (
                  <div key={i} className="aspect-square bg-gray-100 overflow-hidden border border-gray-200 rounded">
                    <img src={`${API_URL}/api/uploads/${path.split('/').pop()}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Damage map */}
        <div className="lg:col-span-2">
          <DamageMap damageZones={vehicle.damage_zones || []} parts={parts} />
        </div>
      </div>

      {/* ─── Parts section ─── */}
      {parts.length > 0 && (
        <div>
          {/* Tabs */}
          <div className="flex items-center gap-0 border-b border-gray-200 mb-1 overflow-x-auto" data-testid="parts-tabs">
            <button
              onClick={() => setActiveTab('recoverable')}
              className={`px-4 md:px-5 py-3 text-xs md:text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'recoverable' ? 'border-[#1B4332] text-[#1B4332]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid="tab-recoverable"
            >
              Pi&egrave;ces r&eacute;cup&eacute;rables
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">{recoverableParts.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('vin')}
              className={`px-4 md:px-5 py-3 text-xs md:text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'vin' ? 'border-[#1B4332] text-[#1B4332]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid="tab-vin"
            >
              {`D\u00e9tect\u00e9es via VIN`}
              {vinParts.length > 0 && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">{vinParts.length}</span>}
            </button>
            <button
              onClick={() => setActiveTab('damaged')}
              className={`px-4 md:px-5 py-3 text-xs md:text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'damaged' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
              data-testid="tab-damaged"
            >
              Hors service
              {damagedParts.length > 0 && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full">{damagedParts.length}</span>}
            </button>
          </div>

          {/* Summary banner */}
          {activeTab === 'damaged' ? (
            <div className="bg-red-50 border border-red-200 px-5 py-3 mb-4 flex items-center gap-2" data-testid="parts-summary-damaged">
              <X size={16} className="text-red-600" weight="bold" />
              <span className="text-sm text-red-800">
                <strong>{damagedParts.length} pi&egrave;ces hors service</strong> {'\u2014'} endommag&eacute;es par le choc, non vendables
              </span>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 px-5 py-3 mb-4 flex items-center gap-2" data-testid="parts-summary">
              <Check size={16} className="text-green-600" weight="bold" />
              <span className="text-sm text-green-800">
                <strong>{recoverableParts.length} pi&egrave;ces r&eacute;cup&eacute;rables</strong> · {goodParts.length} en bon &eacute;tat · valeur estim&eacute;e <strong>{totalValue.toLocaleString('fr-FR')} &euro;</strong>
              </span>
            </div>
          )}

          {/* Search */}
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={"Rechercher une pi\u00e8ce..."}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 text-sm focus:border-[#1B4332] outline-none bg-white"
              data-testid="parts-search"
            />
          </div>

          {/* Part cards */}
          <div className="space-y-3">
            {displayParts.map((part, i) => {
              const realIndex = parts.indexOf(part);
              return (
                <PartCard
                  key={realIndex}
                  part={part}
                  index={realIndex}
                  isEditing={editingPart === realIndex}
                  onEdit={(idx) => { setEditingPart(idx); setEditPrice(part.price || 0); setEditCondition(part.condition || 'occasion'); }}
                  onSave={(idx) => updatePart(idx, { price: parseFloat(editPrice) || 0, condition: editCondition })}
                  onCancel={() => setEditingPart(null)}
                  onMarkSold={markSold}
                  onMarkAvailable={markAvailable}
                  editPrice={editPrice}
                  setEditPrice={setEditPrice}
                  editCondition={editCondition}
                  setEditCondition={setEditCondition}
                />
              );
            })}
            {displayParts.length === 0 && (
              <div className="bg-white border border-gray-200 p-8 text-center shadow-sm">
                <Package size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{searchQuery ? 'Aucune pi\u00e8ce trouv\u00e9e pour cette recherche' : 'Aucune pi\u00e8ce d\u00e9tect\u00e9e'}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ProVehicleDetail;
